import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
	StyledPatientInfoWrapper,
	StyledPatientInfoBlock,
	StyledPatientInfoRow,
} from "./styled/ECGStyle";
import { isMobile } from "react-device-detect";
import capitalizeFirst from '../../../../../util/string';

export default function PatientInfo({ fetchECGData }) {
	const { t } = useTranslation();
	const patientIdName = useSelector((state) => state.patient);
	const { WITH_PATIENT_DISCLOSED } = useSelector((state) => state.config);

	const getDisplayInfo = (patientInfo, ecgStudyInfo) => ({
		PatientID: patientInfo.PatientID,
		PatientName: patientInfo.PatientName,
		manufacturer: ecgStudyInfo.manufacturer,
		StudyDate: ecgStudyInfo.StudyDate,
		StudyTime: ecgStudyInfo.StudyTime,
		modality: ecgStudyInfo.modality,
		lowFrequency: ecgStudyInfo.lowFrequency,
		highFrequency: ecgStudyInfo.highFrequency,
		notchFrequency: ecgStudyInfo.notchFrequency,
		waveFormAnnotations: ecgStudyInfo.annotations,
	});

	const patientInfo = getDisplayInfo(patientIdName, fetchECGData);

	const {
		PatientID,
		PatientName,
		manufacturer,
		StudyDate,
		StudyTime,
		lowFrequency,
		highFrequency,
		notchFrequency,
		waveFormAnnotations,
	} = patientInfo;

	const getAnnotations = () => {
		const annotationData = {};
		waveFormAnnotations.forEach((annotation) => {
			switch (annotation.codeMeaning) {
				case "Ventricular Heart Rate":
					annotationData.ritmoCardiacoVent = annotation.numericValue;
					break;
				case "PR Interval":
					annotationData.prInterval = annotation.numericValue;
					break;
				case "QRS Duration":
					annotationData.qrs = annotation.numericValue;
					break;
				case "QT/QTc":
					annotationData.qtQtc = annotation.numericValue;
					break;
				case "P-QRS-T":
					annotationData.pQrsT = annotation.numericValue;
					break;
				case "Text Observation Value":
					annotationData.textObservation = annotation.stringValue;
					break;
				default:
					return annotationData;
			}
			return annotationData;
		});
		return annotationData;
	};

	const additionalData = getAnnotations();
	const formattedStudyDate = moment(StudyDate).format('YYYY[/]MM[/]DD');
	const formattedStudyTime = StudyTime.replace(/\..+$/, '').match(/.{1,2}/g).join(':');


	return (
		<StyledPatientInfoWrapper>
			<StyledPatientInfoBlock>
				<p>{manufacturer}</p>
				{!isMobile && (
					<StyledPatientInfoRow left>
						<p className="textObservation">{additionalData.textObservation}</p>
					</StyledPatientInfoRow>
				)}
				<StyledPatientInfoRow left>
					<p>{capitalizeFirst(t('studiesTable.studyDate'))}: {formattedStudyDate}</p>
				</StyledPatientInfoRow>
				{formattedStudyTime && (
					<StyledPatientInfoRow left>
						<p>{capitalizeFirst(t('studiesTable.studyTime'))}: {formattedStudyTime}</p>
					</StyledPatientInfoRow>
				)}
			</StyledPatientInfoBlock>
			<StyledPatientInfoBlock>
				<StyledPatientInfoRow left>
					{additionalData.ritmoCardiacoVent ? (
						<p>
							{t('ventricular.heart.rhythm')}: {additionalData.ritmoCardiacoVent} bpm{" "}
						</p>
					) : (
						<p>{t('ventricular.heart.rhythm')}:</p>
					)}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow left>
					{additionalData.prInterval ? (
						<p>{t('pr.interval')}: {additionalData.prInterval} ms </p>
					) : (
						<p>{t('pr.interval')}:</p>
					)}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow left>
					{" "}
					{additionalData.qrs ? (
						<p>
							{t('qrs.duration')}:
							{additionalData.qrs} ms{" "}
						</p>
					) : (
						<p>{t('qrs.duration')}:</p>
					)}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow left>
					{" "}
					{additionalData.qtQtc ? (
						<p>
							QT/QTc:
							{additionalData.qtQtc} ms{" "}
						</p>
					) : (
						<p>QT/QTc:</p>
					)}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow left>
					{additionalData.pQrsT ? (
						<p>{t('p.qrs.t.axes')}: {additionalData.pQrsT} </p>
					) : (
						<p>{t('p.qrs.t.axes')}:</p>
					)}
				</StyledPatientInfoRow>
			</StyledPatientInfoBlock>

			<StyledPatientInfoBlock right>
				<StyledPatientInfoRow right>
					{WITH_PATIENT_DISCLOSED && <p>{(t('name'))}: {PatientName}</p>}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow right>
					{WITH_PATIENT_DISCLOSED && <p>ID: {PatientID}</p>}
				</StyledPatientInfoRow>
				<StyledPatientInfoRow right>
					<p>
						LF: {lowFrequency} HF: {highFrequency} NF: {notchFrequency}
					</p>
				</StyledPatientInfoRow>
			</StyledPatientInfoBlock>
		</StyledPatientInfoWrapper>
	);
}
